import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  PlusCircle, 
  FileEdit, 
  Trash, 
  Eye, 
  Clock, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  User,
  Users
} from "lucide-react";
import { examsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import AddExamModal from "@/components/admin/AddExamModal";
import ViewExamModal from "@/components/admin/ViewExamModal";
import EditExamModal from "@/components/admin/EditExamModal";
import ModernPageTemplate, { 
  ModernBadge, 
  ModernLoading,
  ModernPaginationFooter
} from "@/components/common/ModernPageTemplate";

const Exams = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Constants
  const EXAMS_PER_PAGE = 10;
  
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("my");
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  
  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(EXAMS_PER_PAGE);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);

  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Effect to adjust footer position based on sidebar state
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.admin-fixed-footer');
      if (!footer) return;

      if (window.innerWidth >= 1024) {
        const mainContent = document.querySelector('[class*="lg:pl-"]');
        if (mainContent) {
          const classes = mainContent.className;
          if (classes.includes('lg:pl-14')) {
            footer.style.left = '68px';
          } else if (classes.includes('lg:pl-64')) {
            footer.style.left = '256px';
          }
        }
      } else {
        footer.style.left = '0px';
      }
    };

    adjustFooterPosition();
    window.addEventListener('resize', adjustFooterPosition);

    const observer = new MutationObserver(adjustFooterPosition);
    const mainContent = document.querySelector('[class*="lg:pl-"]');
    if (mainContent) {
      observer.observe(mainContent, { 
        attributes: true, 
        attributeFilter: ['class'] 
      });
    }

    return () => {
      window.removeEventListener('resize', adjustFooterPosition);
      observer.disconnect();
    };
  }, []);

  // Unified query for exams based on active tab
  const {
    data: examsData,
    isLoading: examsLoading,
    isError: examsError,
    refetch: refetchExams,
  } = useQuery({
    queryKey: ["exams", currentPage, searchTerm, activeTab, rowsPerPage, isPaginationEnabled],
    queryFn: () => {
      const params = {
        page: isPaginationEnabled ? currentPage : 1,
        limit: isPaginationEnabled ? rowsPerPage : 999999,
        search: searchTerm,
        filter: activeTab,
      };
      return examsAPI.getAll(params);
    },
    keepPreviousData: true,
  });

  // Reset page when search/tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  // Refresh function
  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    setActiveTab('my');
    setSortField('');
    setSortOrder('asc');
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedExam(null);
    
    await queryClient.invalidateQueries({ queryKey: ["exams"] });
    
    toast({
      title: "Refreshed",
      description: "Data has been refreshed successfully",
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePaginationToggle = (enabled) => {
    setIsPaginationEnabled(enabled);
    if (!enabled) {
      setCurrentPage(1);
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortExams = (exams, sortField, sortOrder) => {
    if (!sortField) return exams;

    return [...exams].sort((a, b) => {
      let aValue, bValue;

      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      if (sortField === 'createdDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleViewExam = (exam) => {
    setSelectedExam(exam);
    setIsViewModalOpen(true);
  };

  const handleEditExam = async (exam) => {
    try {
      const res = await examsAPI.canEdit(exam._id);

      if (!res.data.canEdit) {
        toast({
          title: "Cannot edit exam",
          description:
            "This exam has been assigned and not all candidates have completed, been disqualified, or missed the exam.",
          variant: "destructive",
        });
        return;
      }

      setSelectedExam(exam);
      setIsEditModalOpen(true);
    } catch (error) {
      console.error("Edit check failed:", error);
      toast({
        title: "Error",
        description: "Failed to verify edit eligibility.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteExam = (exam) => {
    setExamToDelete(exam);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteExam = async () => {
    if (!examToDelete) return;

    try {
      await examsAPI.delete(examToDelete._id);
      toast({
        title: "Exam deleted",
        description: "The exam has been permanently deleted",
      });
      
      refetchExams();
      setIsDeleteDialogOpen(false);
      setExamToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete exam",
        variant: "destructive",
      });
    }
  };

  // Render exams table
  const renderExamsTable = () => {
    const exams = examsData?.data?.exams || [];
    const pagination = examsData?.data || {
      totalPages: 1,
      currentPage: 1,
      total: 0,
    };

    const sortedExams = sortExams(exams, sortField, sortOrder);

    return (
      <>
        {examsLoading ? (
          <ModernLoading text="Loading exams..." />
        ) : examsError ? (
          <div className="text-center p-6 text-red-500">
            Error loading exams. Please try again.
          </div>
        ) : sortedExams.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead className="admin-table-header">
                <tr>
                  <th className="admin-table-cell">S.No</th>
                  <th 
                    className="admin-table-cell admin-sortable-header"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    className="admin-table-cell admin-sortable-header"
                    onClick={() => handleSort('questions')}
                  >
                    <div className="flex items-center">
                      Questions
                      {getSortIcon('questions')}
                    </div>
                  </th>
                  <th 
                    className="admin-table-cell admin-sortable-header"
                    onClick={() => handleSort('totalTime')}
                  >
                    <div className="flex items-center">
                      Duration
                      {getSortIcon('totalTime')}
                    </div>
                  </th>
                  <th 
                    className="admin-table-cell admin-sortable-header"
                    onClick={() => handleSort('language')}
                  >
                    <div className="flex items-center">
                      Language
                      {getSortIcon('language')}
                    </div>
                  </th>
                  <th 
                    className="admin-table-cell admin-sortable-header"
                    onClick={() => handleSort('isTemplate')}
                  >
                    <div className="flex items-center">
                      Template
                      {getSortIcon('isTemplate')}
                    </div>
                  </th>
                  <th 
                    className="admin-table-cell admin-sortable-header"
                    onClick={() => handleSort('createdBy.name')}
                  >
                    <div className="flex items-center">
                      Created By
                      {getSortIcon('createdBy.name')}
                    </div>
                  </th>
                  <th 
                    className="admin-table-cell admin-sortable-header"
                    onClick={() => handleSort('createdDate')}
                  >
                    <div className="flex items-center">
                      Created On
                      {getSortIcon('createdDate')}
                    </div>
                  </th>
                  <th className="admin-table-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedExams.map((exam, index) => (
                  <tr key={exam._id} className="admin-table-row">
                    <td className="admin-table-cell number">
                      {isPaginationEnabled ? (currentPage - 1) * rowsPerPage + index + 1 : index + 1}
                    </td>
                    <td className="admin-table-cell font-medium">
                      <span className="text-sm">{exam.name}</span>
                    </td>
                    <td className="admin-table-cell">
                      <ModernBadge variant="language">
                        <span className="text-xs">{exam.questions?.length || 0}</span>
                      </ModernBadge>
                    </td>
                    <td className="admin-table-cell">
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" />
                        <span className="text-sm">{exam.totalTime} min</span>
                      </div>
                    </td>
                    <td className="admin-table-cell">
                      <ModernBadge variant="template">
                        <span className="text-xs">{exam.language || "English"}</span>
                      </ModernBadge>
                    </td>
                    <td className="admin-table-cell">
                      {exam.isTemplate ? (
                        <ModernBadge variant="language">
                          <span className="text-xs">Yes</span>
                        </ModernBadge>
                      ) : (
                        <span className="text-gray-400 text-xs">No</span>
                      )}
                    </td>
                    <td className="admin-table-cell">
                      {exam.createdBy ? (
                        <ModernBadge variant="template">
                          <span className="text-xs">{exam.createdBy.name || "Unknown"}</span>
                        </ModernBadge>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                    <td className="admin-table-cell">
                      <span className="text-xs">
                        {exam.createdDate ? format(new Date(exam.createdDate), "MMM dd, yyyy") : "N/A"}
                      </span>
                    </td>
                    <td className="admin-table-cell text-right">
                      <div className="admin-table-actions">
                        <button
                          className="admin-table-action-btn view"
                          onClick={() => handleViewExam(exam)}
                          title="View exam"
                        >
                          <Eye className="h-3 w-3" />
                        </button>
                        <button
                          className="admin-table-action-btn edit"
                          onClick={() => handleEditExam(exam)}
                          title="Edit exam"
                        >
                          <FileEdit className="h-3 w-3" />
                        </button>
                        <button
                          className="admin-table-action-btn delete"
                          onClick={() => handleDeleteExam(exam)}
                          title="Delete exam"
                        >
                          <Trash className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center p-6 text-gray-500">
            No exams found
          </div>
        )}
      </>
    );
  };

  return (
    <ModernPageTemplate
      title="Exams"
      contentClassName="exams-page"
      stats={{
        total: examsData?.data?.total || 0,
        customStats: (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="categories-header-tabs-list">
              <TabsTrigger value="my" className="categories-header-tab">
                <User className="h-3 w-3" />
                My Exams
              </TabsTrigger>
              <TabsTrigger value="company" className="categories-header-tab">
                <Users className="h-3 w-3" />
                Company Exams
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )
      }}
      onRefresh={handleRefresh}
      search={{
        placeholder: "Search exams by name...",
        value: searchTerm,
        onChange: handleSearchChange,
        onSubmit: (e) => e.preventDefault()
      }}
      headerAction={
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsAddModalOpen(true)}
          title="Create New Exam"
        >
          <PlusCircle className="w-4 h-4" />
        </Button>
      }
    >
      {renderExamsTable()}

      {/* Modern Pagination Footer */}
      <ModernPaginationFooter
        isPaginationEnabled={isPaginationEnabled}
        onPaginationToggle={handlePaginationToggle}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={{
          current: currentPage,
          pages: examsData?.data?.totalPages || 1,
          total: examsData?.data?.total || 0
        }}
        onPageChange={handlePageChange}
        rowsOptions={[5, 10, 20, 50]}
      />

      <AddExamModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => {
          setCurrentPage(1);
          setSortField('');
          setSortOrder('asc');
          refetchExams();
          setIsAddModalOpen(false);
          toast({
            title: "Exam created",
            description: "The exam has been created successfully and appears at the top of the list",
          });
        }}
      />

      <ViewExamModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        exam={selectedExam}
      />

      <EditExamModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        examId={selectedExam?._id}
        onSuccess={() => {
          refetchExams();
          setIsEditModalOpen(false);
          toast({
            title: "Exam updated",
            description: "The exam has been updated successfully",
          });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Exam?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the exam "{examToDelete?.name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setExamToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteExam}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ModernPageTemplate>
  );
};

export default Exams;
